/**
 * 上传通道（S07）：把环境素材 / 本体模型 / 数据集 / 策略权重直传 COS。
 *
 * 页面只做「选用途 → 拖文件 → 看进度 → 看结果」，重活都在 lib/directUpload.ts：
 * 签发 → 分片直传（带进度与续传）→ 服务端 HEAD 校验后置为 completed。
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Popconfirm,
  Progress,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { callApi, ApiError } from "../lib/api";
import { useProject } from "../lib/project";
import { useDirectUpload, type Purpose } from "../lib/directUpload";
import { AsyncBoundary } from "../components/AsyncBoundary";

const PURPOSE_LABEL: Record<Purpose, string> = {
  env_source: "环境素材（图片/视频/PLY）",
  robot_model: "本体模型（URDF/Mesh）",
  dataset: "数据集（rosbag/mcap/zip）",
  policy_ckpt: "策略权重（ckpt/safetensors）",
};

const STATUS_COLOR: Record<string, string> = {
  pending: "processing",
  completed: "success",
  aborted: "default",
  expired: "warning",
};

interface UploadRow {
  id: string;
  filename: string;
  purpose: Purpose;
  mode: string;
  status: string;
  declared_size: number;
  actual_size: number | null;
  object_key: string;
  created_at: string;
  completed_at: string | null;
}

interface ListData {
  items: UploadRow[];
  total: number | null;
  page: number;
  page_size: number;
}

const fmtSize = (n: number | null | undefined) => {
  if (!n) return "-";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const fmtTime = (s: string | null | undefined) => (s ? new Date(s).toLocaleString("zh-CN") : "-");

export function UploadsPage() {
  const { projectId } = useProject();
  const qc = useQueryClient();
  const [purpose, setPurpose] = useState<Purpose>("env_source");
  const [page, setPage] = useState(1);
  const { state, upload, reset } = useDirectUpload();

  const q = useQuery({
    queryKey: ["uploads", projectId, page],
    queryFn: async () => {
      const params = new URLSearchParams({ project_id: projectId ?? "", page: String(page), page_size: "20" });
      return (await callApi<ListData>(`/api/uploads?${params.toString()}`)).data;
    },
    enabled: !!projectId,
    refetchInterval: state.phase === "uploading" ? 5000 : false,
  });

  const del = useMutation({
    mutationFn: async (id: string) => (await callApi(`/api/uploads/${id}`, { method: "DELETE" })).data,
    onSuccess: () => {
      message.success("已删除（含 COS 对象）");
      void qc.invalidateQueries({ queryKey: ["uploads"] });
    },
    onError: (e) => message.error(e instanceof ApiError ? `${e.message}（code=${e.code}）` : String(e)),
  });

  const onPick = async (file: File) => {
    if (!projectId) {
      message.warning("请先在顶部选择项目");
      return false;
    }
    reset();
    const r = await upload(file, { projectId, purpose });
    if (r) {
      message.success("上传成功并通过服务端校验");
      void qc.invalidateQueries({ queryKey: ["uploads"] });
    } else {
      message.error("上传失败，详情见下方进度区");
    }
    return false;
  };

  const columns: ColumnsType<UploadRow> = [
    {
      title: "文件",
      dataIndex: "filename",
      render: (v: string, r) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{v}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {r.object_key}
          </Typography.Text>
        </Space>
      ),
    },
    { title: "用途", dataIndex: "purpose", width: 180, render: (v: Purpose) => PURPOSE_LABEL[v] ?? v },
    {
      title: "大小",
      dataIndex: "declared_size",
      width: 110,
      render: (v: number, r) => (
        <Space direction="vertical" size={0}>
          <span>{fmtSize(v)}</span>
          {r.actual_size != null && r.actual_size !== v && (
            <Typography.Text type="danger" style={{ fontSize: 12 }}>
              实际 {fmtSize(r.actual_size)}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    { title: "模式", dataIndex: "mode", width: 90, render: (v: string) => (v === "multipart" ? "分段" : "单次") },
    {
      title: "状态",
      dataIndex: "status",
      width: 100,
      render: (v: string) => <Tag color={STATUS_COLOR[v]}>{v}</Tag>,
    },
    { title: "创建时间", dataIndex: "created_at", width: 170, render: (v: string) => fmtTime(v) },
    {
      title: "操作",
      width: 90,
      render: (_, r) => (
        <Popconfirm title="删除会话与 COS 对象？" onConfirm={() => del.mutate(r.id)}>
          <Button size="small" danger type="link">
            删除
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const uploading = state.phase === "signing" || state.phase === "uploading" || state.phase === "verifying";

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        上传通道
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        文件由浏览器直传对象存储（不经云函数），服务端只签发一条绑定路径的临时凭证；传完会 HEAD 回读校验大小，
        校验通过才算「已就绪」。大文件自动分段，网络中断后重新选择同一个文件会从断点续传。
      </Typography.Paragraph>

      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Space wrap>
            <span>用途</span>
            <Select
              value={purpose}
              onChange={setPurpose}
              style={{ width: 260 }}
              disabled={uploading}
              options={Object.entries(PURPOSE_LABEL).map(([value, label]) => ({ value, label }))}
            />
          </Space>

          <Upload.Dragger
            multiple={false}
            showUploadList={false}
            disabled={uploading || !projectId}
            beforeUpload={(file) => {
              void onPick(file as unknown as File);
              return false;
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽文件到此处上传</p>
            <p className="ant-upload-hint">
              {projectId ? "单个文件；大文件自动分段，支持断点续传" : "请先在顶部选择项目"}
            </p>
          </Upload.Dragger>

          {state.phase !== "idle" && (
            <div>
              <Progress
                percent={state.percent}
                status={
                  state.phase === "error" ? "exception" : state.phase === "done" ? "success" : "active"
                }
                format={(p) => `${p ?? 0}%`}
              />
              <Space size={16} wrap>
                <Typography.Text type="secondary">
                  {state.phase === "signing" && "正在签发凭证…"}
                  {state.phase === "uploading" &&
                    `上传中 ${fmtSize(state.loaded)} / ${fmtSize(state.total)}${
                      state.partTotal > 1 ? `（分片 ${state.partDone}/${state.partTotal}）` : ""
                    }`}
                  {state.phase === "verifying" && "服务端校验中（HEAD 回读）…"}
                  {state.phase === "done" && "完成，已通过服务端大小校验"}
                  {state.phase === "error" && `失败：${state.error ?? "未知错误"}`}
                </Typography.Text>
                {state.resumed && <Tag color="blue">已复用上次未完成的上传</Tag>}
              </Space>
              {state.phase === "error" && state.total > 0 && (
                <Alert
                  style={{ marginTop: 8 }}
                  type="info"
                  showIcon
                  message="重新选择同一个文件即可从断点继续（有效期 1 小时）"
                />
              )}
            </div>
          )}
        </Space>
      </Card>

      <Card title={`上传记录${q.data?.total != null ? `（${q.data.total}）` : ""}`}>
        <AsyncBoundary
          loading={q.isLoading}
          error={q.error}
          empty={!q.data?.items?.length}
          emptyText="还没有上传记录"
          onRetry={() => void q.refetch()}
        >
          <Table
            rowKey="id"
            size="small"
            columns={columns}
            dataSource={q.data?.items ?? []}
            pagination={{
              current: page,
              pageSize: 20,
              total: q.data?.total ?? 0,
              onChange: setPage,
              showSizeChanger: false,
            }}
          />
        </AsyncBoundary>
      </Card>
    </div>
  );
}
